'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import Button from '@/components/ui/Button';
import Modal, { ModalCloseButton } from '@/components/ui/Modal';

interface DialogOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface PromptOptions extends DialogOptions {
  defaultValue?: string;
  inputType?: React.HTMLInputTypeAttribute;
  placeholder?: string;
}

interface DialogApi {
  alert: (message: string, options?: DialogOptions) => Promise<void>;
  confirm: (message: string, options?: DialogOptions) => Promise<boolean>;
  prompt: (
    message: string,
    options?: PromptOptions,
  ) => Promise<string | null>;
}

type DialogResult = boolean | string | null | undefined;

interface DialogRequest {
  kind: 'alert' | 'confirm' | 'prompt';
  message: string;
  options: PromptOptions;
  resolve: (value: DialogResult) => void;
}

const DialogContext = createContext<DialogApi | null>(null);

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = useState<DialogRequest | null>(null);
  const [promptValue, setPromptValue] = useState('');

  const openDialog = useCallback(
    (
      kind: DialogRequest['kind'],
      message: string,
      options: PromptOptions = {},
    ) =>
      new Promise<DialogResult>((resolve) => {
        setPromptValue(options.defaultValue ?? '');
        setRequest({ kind, message, options, resolve });
      }),
    [],
  );

  const api = useMemo<DialogApi>(
    () => ({
      alert: async (message, options) => {
        await openDialog('alert', message, options);
      },
      confirm: async (message, options) =>
        (await openDialog('confirm', message, options)) === true,
      prompt: async (message, options) => {
        const result = await openDialog('prompt', message, options);
        return typeof result === 'string' ? result : null;
      },
    }),
    [openDialog],
  );

  const finish = useCallback(
    (value: DialogResult) => {
      if (!request) return;
      const { resolve } = request;
      setRequest(null);
      resolve(value);
    },
    [request],
  );

  const cancel = useCallback(() => {
    if (request?.kind === 'confirm') finish(false);
    else if (request?.kind === 'prompt') finish(null);
    else finish(undefined);
  }, [finish, request?.kind]);

  const title =
    request?.options.title ??
    (request?.kind === 'alert'
      ? '알림'
      : request?.kind === 'prompt'
        ? '입력'
        : '확인');

  return (
    <DialogContext.Provider value={api}>
      {children}
      {request && (
        <Modal
          onClose={cancel}
          ariaLabel={title}
          showCloseButton={false}
          panelClassName="w-full max-w-md overflow-hidden rounded-xl"
        >
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="pt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
            <ModalCloseButton onClick={cancel} />
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              finish(request.kind === 'prompt' ? promptValue : true);
            }}
          >
            <div className="space-y-4 px-6 py-5">
              <p className="whitespace-pre-line text-sm leading-6 text-gray-700 dark:text-gray-300">
                {request.message}
              </p>
              {request.kind === 'prompt' && (
                <input
                  autoFocus
                  type={request.options.inputType ?? 'text'}
                  value={promptValue}
                  placeholder={request.options.placeholder}
                  onChange={(event) => setPromptValue(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 bg-gray-50 px-6 py-4 dark:bg-gray-800/60">
              {request.kind !== 'alert' && (
                <Button type="button" variant="outline" onClick={cancel}>
                  {request.options.cancelLabel ?? '취소'}
                </Button>
              )}
              <Button
                type="submit"
                variant={request.options.danger ? 'danger' : 'primary'}
              >
                {request.options.confirmLabel ??
                  (request.kind === 'alert' ? '확인' : '계속')}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
}
